import * as React from 'react'

import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from 'lucide-react'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import SelectWrapper from '~/client/src/desktop/components/SelectWrapper/SelectWrapper'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { enumToList } from '~/client/src/shared/utils/converters'
import { NOOP } from '~/client/src/shared/utils/noop'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

// localization: translated

interface IProps<T> {
  items: T[]
  newItemIcon: JSX.Element
  newItemText: string
  onNewItemClick(): void
  renderActiveItems(items: T[]): JSX.Element
  renderDoneItems(items: T[]): JSX.Element
  doneFilter(item: T): boolean
  dateRangeFilter(item: T, startDate: Date, endDate: Date): boolean

  onModalClose?(): void

  isNewItemBtnHidden?: boolean
  isNewItemBtnDisabled?: boolean
  isNewValueShown?: boolean
  isTotalDoneValueShown?: boolean
  isDateRangeShown?: boolean

  doneTableText?: string
  getTotalDoneValue?(doneItems: T[]): React.ReactText

  projectDateStore?: ProjectDateStore
}

enum DataDateRange {
  ALL = 'All',
  THIS_WEEK = 'This week',
  THIS_MONTH = 'This month',
}
const dataDateRangeList = enumToList(DataDateRange)
const getDateRangeCaption = (range: DataDateRange): string => {
  switch (range) {
    case DataDateRange.ALL:
      return Localization.translator.all_items
    case DataDateRange.THIS_WEEK:
      return Localization.translator.thisWeek
    case DataDateRange.THIS_MONTH:
      return Localization.translator.thisMonth
  }
}

const DEFAULT_ICON_SIZE = 16

export const BaseDataCellCloseContext = React.createContext<() => void>(NOOP)

@inject('projectDateStore')
@observer
export default class BaseDataCell<T> extends React.Component<IProps<T>> {
  @observable private dataDateRange = DataDateRange.ALL
  @observable private areDoneItemsShown: boolean = false
  @observable private isPopoverOpen: boolean = false
  @observable private items: T[] = []
  @observable private projectDateStore: ProjectDateStore = null
  @observable private dateRangeFilter: (
    item: T,
    startDate: Date,
    endDate: Date,
  ) => boolean

  public constructor(props: IProps<T>) {
    super(props)

    this.items = props.items
    this.projectDateStore = props.projectDateStore
    this.dateRangeFilter = props.dateRangeFilter
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps<T>) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.items = this.props.items
        this.projectDateStore = this.props.projectDateStore
        this.dateRangeFilter = this.props.dateRangeFilter
      })
    }
  }

  public render() {
    if (!this.props.items?.length) {
      return this.noValueRenderer
    }

    return (
      <Popover
        open={this.isPopoverOpen}
        onOpenChange={this.setPopoverOpen}
        onOpenChangeComplete={this.onPopoverOpenChangeComplete}
      >
        <PopoverTrigger
          nativeButton={false}
          render={<div className="full-width" />}
        >
          {this.props.children}
        </PopoverTrigger>
        <PopoverContent side="bottom" className="form-cell-modal tw:w-auto">
          <BaseDataCellCloseContext.Provider value={this.closePopover}>
            {this.itemsDataTable}
          </BaseDataCellCloseContext.Provider>
        </PopoverContent>
      </Popover>
    )
  }

  private get noValueRenderer(): JSX.Element {
    const { isNewValueShown, onNewItemClick } = this.props
    if (!isNewValueShown) {
      return <span>{NO_VALUE}</span>
    }

    return (
      <div onClick={this.stopPropagation}>
        <div
          className="tw:flex tw:items-center underline-hover form-cell-value pointer h48 no-select"
          onClick={onNewItemClick}
        >
          <PlusIcon className="tw:mr-1 tw:size-4 tw:shrink-0" />
          {Localization.translator.new}
        </div>
      </div>
    )
  }

  private get itemsDataTable(): JSX.Element {
    const {
      renderActiveItems,
      isDateRangeShown,
      newItemIcon,
      newItemText,
      isNewItemBtnHidden,
      isNewItemBtnDisabled,
    } = this.props

    return (
      <div className="py10 px10 col">
        <div className="row x-between h36 m5 pl10">
          {!isNewItemBtnHidden && (
            <div
              className={classList({
                'row no-grow text large blue-highlight pointer underline-hover': true,
                'inactive-element': isNewItemBtnDisabled,
              })}
              onClick={this.onNewItemClick}
            >
              {newItemIcon}
              <span className="mr5 no-grow nowrap">{newItemText}</span>
            </div>
          )}
          {isDateRangeShown && (
            <SelectWrapper className="no-grow mr10">
              <select
                value={this.dataDateRange}
                onChange={this.changeDateRange}
                className="pl12 pr25 py6 ba-none no-outline bg-inherit text large light no-grow pointer"
              >
                {dataDateRangeList.map(range => (
                  <option key={range} value={range}>
                    {getDateRangeCaption(range)}
                  </option>
                ))}
              </select>
            </SelectWrapper>
          )}
        </div>
        {renderActiveItems(this.activeItems)}
        {this.doneItemsTable}
      </div>
    )
  }

  private get doneItemsTable(): JSX.Element {
    if (!this.doneItems.length) {
      return null
    }

    const {
      renderDoneItems,
      isTotalDoneValueShown,
      getTotalDoneValue,
      doneTableText,
    } = this.props
    const ToggleIcon = this.areDoneItemsShown
      ? ChevronDownIcon
      : ChevronRightIcon

    return (
      <>
        <div
          className={classList({
            'row h36 px10 pointer no-select': true,
            mt15: !!this.activeItems.length,
          })}
          onClick={this.toggleDoneItems}
        >
          <div className="no-flex mr50 mw300 text large faded">
            <ToggleIcon
              className="tw:mr-1 tw:inline-block tw:align-text-bottom"
              size={DEFAULT_ICON_SIZE}
            />
            {doneTableText} ({this.doneItems.length})
          </div>
          {isTotalDoneValueShown && !this.areDoneItemsShown && (
            <>
              <div className="mw80 ml30 no-flex text large faded end">
                {getTotalDoneValue(this.doneItems)}
              </div>
              <div />
            </>
          )}
        </div>
        {this.areDoneItemsShown && renderDoneItems(this.doneItems)}
      </>
    )
  }

  private get activeItems(): T[] {
    return this.filteredItems.filter(i => !this.props.doneFilter(i))
  }

  private get doneItems(): T[] {
    return this.filteredItems.filter(this.props.doneFilter)
  }

  @computed
  private get filteredItems(): T[] {
    const { startOfWeek, endOfWeek, startOfMonth, endOfMonth } =
      this.projectDateStore

    const dateNow = Date.now()
    let startDate: Date, endDate: Date

    switch (this.dataDateRange) {
      case DataDateRange.ALL:
        return this.items
      case DataDateRange.THIS_WEEK:
        startDate = startOfWeek(dateNow)
        endDate = endOfWeek(dateNow)
        return this.items.filter(i =>
          this.dateRangeFilter(i, startDate, endDate),
        )
      case DataDateRange.THIS_MONTH:
        startDate = startOfMonth(dateNow)
        endDate = endOfMonth(dateNow)
        return this.items.filter(i =>
          this.dateRangeFilter(i, startDate, endDate),
        )
      default:
        return []
    }
  }

  private stopPropagation = (event: React.SyntheticEvent) => {
    event?.stopPropagation()
    event?.preventDefault()
  }

  @action.bound
  private changeDateRange(event: React.ChangeEvent<HTMLSelectElement>) {
    this.dataDateRange = event.target.value as DataDateRange
  }

  @action.bound
  private toggleDoneItems() {
    this.areDoneItemsShown = !this.areDoneItemsShown
  }

  @action.bound
  private setPopoverOpen(isOpen: boolean) {
    this.isPopoverOpen = isOpen
  }

  private closePopover = () => this.setPopoverOpen(false)

  private onNewItemClick = () => {
    this.closePopover()
    this.props.onNewItemClick()
  }

  private onPopoverOpenChangeComplete = (isOpen: boolean) => {
    if (!isOpen) {
      this.resetAllValues()
    }
  }

  @action.bound
  private resetAllValues() {
    this.dataDateRange = DataDateRange.ALL
    this.areDoneItemsShown = false
    this.props.onModalClose?.()
  }
}
